import { useState, useRef } from 'react';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import { getLabelFromJspVariables } from '../common/JspUtil';
import ReCAPTCHA from 'react-google-recaptcha';
import { PageHeader, TextInput } from '../components';
import { useLoader, useJspVariables } from '../hooks';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

const PhoneVerifier = ({ userProfile, authenticatedUser, which, phoneNumber, phoneCode, onConfirm, onCancel }) => {
    const { showLoader, hideLoader } = useLoader();
    const { jspVariables } = useJspVariables();
    const [captchaResponse, setCaptchaResponse] = useState('');
    const [sent, setSent] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [service, setService] = useState('Voice');
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});
    const ignoreCaptcha = jspVariables.jspDisableCaptcha === 'Y';

    const recaptchaInstance = useRef(null);

    const resetCaptcha = () => {
        if (recaptchaInstance.current) {
            recaptchaInstance.current.reset();
        }
        setCaptchaResponse('');
    };

    const sendVerificationCode = async () => {
        setAlert(null);
        if (!ignoreCaptcha && !captchaResponse) {
            setAlert({ message: getLabelFromJspVariables('alert_captcha_required'), severity: 'error' });
            return;
        }
        const p = { ...userProfile, which, service, phoneCode, phoneNumber };
        setSent(true);
        showLoader('Sending verification code...');
        try {
            await actions.sendPhoneVerificationCode(
                authenticatedUser.user, authenticatedUser.app, p, captchaResponse);
            setAlert({ message: getLabelFromJspVariables('alert_phone_code_sent'), severity: 'success' });
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        } finally {
            hideLoader();
        }
        resetCaptcha();
    };

    const confirmPhone = async () => {
        setAlert(null);
        if (!verificationCode.trim()) {
            setErrors({ verificationCode: errorCodes.MSG_FIELD_REQUIRED() });
            return;
        }
        setErrors({});
        const phoneData = { which, service, phoneCode, phoneNumber, verificationCode };
        showLoader('Verifying phone...');
        try {
            const resp = await actions.confirmPhone(
                authenticatedUser.user, authenticatedUser.app, userProfile, phoneData);
            onConfirm(resp.data);
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 1 }}>
                {getLabelFromJspVariables('note_phone_verify_info')}
            </Alert>
            {alert && <Alert id="phoneVerifierAlert" data-automation-id="phone_verifier_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Typography variant="body2">{getLabelFromJspVariables('label_phone_to_verify')}</Typography>
                <Typography variant="body2" fontWeight={700}>{`${phoneCode} ${phoneNumber}`}</Typography>
            </Box>

            <FormControl>
                <FormLabel sx={{ fontSize: '0.85rem' }}>{getLabelFromJspVariables('label_select_message_type')}</FormLabel>
                <RadioGroup value={service} onChange={event => setService(event.target.value)} row>
                    <FormControlLabel value="Voice" control={<Radio size="small" inputProps={{ 'data-automation-id': 'voice' }} />} label={getLabelFromJspVariables('label_voice_message')} />
                    <FormControlLabel value="SMS" control={<Radio size="small" inputProps={{ 'data-automation-id': 'sms' }} />} label={getLabelFromJspVariables('label_text_message')} />
                </RadioGroup>
            </FormControl>

            {!ignoreCaptcha && <Box sx={{ mt: 1 }}>
                <ReCAPTCHA sitekey={constants.GOOGLE_RECAPTCHA_V2_SITE_KEY}
                    ref={recaptchaInstance}
                    onChange={val => setCaptchaResponse(val)}
                />
            </Box>}

            {sent && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextInput
                            label={getLabelFromJspVariables('label_verification_code')}
                            name="verificationCode"
                            id="verification_code_input"
                            dataAutomationId="verification_code_input"
                            autoComplete="one-time-code"
                            required
                            numericOnly maxLength={6}
                            value={verificationCode}
                            error={!!errors.verificationCode}
                            helperText={errors.verificationCode || ''}
                            onChange={e => { setVerificationCode(e.target.value); if (errors.verificationCode) setErrors({}); }}
                            sx={{ flex: 1 }}
                        />
                        <Button variant="outlined" onClick={sendVerificationCode} sx={{ mt: 2.5 }}
                            data-automation-id="phone_verifier_resend_btn">
                            {getLabelFromJspVariables('action_resend')}
                        </Button>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button variant="outlined" onClick={onCancel} data-automation-id="phone_verifier_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                {sent ?
                    <Button variant="contained" onClick={confirmPhone} data-automation-id="phone_verifier_confirm_btn">{getLabelFromJspVariables('action_confirm')}</Button>
                    :
                    <Button variant="contained" onClick={sendVerificationCode} data-automation-id="phone_verifier_send_btn">{getLabelFromJspVariables('action_send')}</Button>
                }
            </Box>
        </Box>
    );
};

export default PhoneVerifier;
